import React from 'react';
import LineChart from '@cloudscape-design/components/line-chart';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import html2canvas from 'html2canvas';

import { ResultRow } from '../api/ForecastApi';
import { ChartSpec } from '../views/types';
import {
  resolveColumn,
  capToTopGroups,
  distinctGroups,
  MAX_GROUPS,
} from '../utils/groups';
import { todayString } from '../utils/date';

interface ResultChartProps {
  rows: ResultRow[];
  spec: ChartSpec;
  viewType?: string;
  business?: string;
  region?: string;
}

type Series = { title: string; type: 'line'; data: { x: string; y: number }[] };

function buildSeries(
  rows: ResultRow[],
  dateCol: string,
  valueCol: string,
  groupCol?: string
): { series: Series[]; xDomain: string[] } {
  const xDomain = Array.from(
    new Set(rows.map((r) => String(r[dateCol])))
  ).sort();

  const groups = new Map<string, { x: string; y: number }[]>();
  rows.forEach((row) => {
    const group = groupCol && row[groupCol] != null ? String(row[groupCol]) : 'All';
    const point = { x: String(row[dateCol]), y: Number(row[valueCol]) || 0 };
    const existing = groups.get(group);
    if (existing) existing.push(point);
    else groups.set(group, [point]);
  });

  const series: Series[] = Array.from(groups.entries()).map(([title, data]) => ({
    title,
    type: 'line',
    data: data.sort((a, b) => a.x.localeCompare(b.x)),
  }));

  return { series, xDomain };
}

// Legend filter label prefix.
function groupNoun(groupCol?: string): string {
  if (!groupCol) return 'Series';
  if (/fgid/i.test(groupCol)) return 'FGIDs';
  if (/site/i.test(groupCol)) return 'Sites';
  return 'Groups';
}

async function downloadChartPng(el: HTMLElement | null, fileName: string): Promise<void> {
  if (!el) return;
  const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
  const link = document.createElement('a');
  link.href = canvas.toDataURL('image/png');
  link.download = fileName;
  link.click();
}

export default function ResultChart({
  rows,
  spec,
  viewType = 'view',
  business = '',
  region = '',
}: ResultChartProps): JSX.Element {
  const chartRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Shared series visibility across charts, by title; null = all visible.
  const [visibleTitles, setVisibleTitles] = React.useState<string[] | null>(null);

  const keys = React.useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [rows]);

  const dateCol = resolveColumn(keys, spec.dateColumn, spec.dateMatch);
  const groupCol = spec.groupColumn
    ? resolveColumn(keys, spec.groupColumn, spec.groupMatch)
    : undefined;

  const charts = spec.metrics
    .map((m) => ({ title: m.title, col: resolveColumn(keys, m.column, m.match) }))
    .filter((c): c is { title: string; col: string } => Boolean(c.col));

  const { rows: displayRows, total, capped } = capToTopGroups(rows, groupCol);

  // Group values shared by every chart.
  const allTitles = React.useMemo(
    () => (groupCol ? distinctGroups(displayRows, groupCol) : ['All']),
    [displayRows, groupCol]
  );

  // Reset visibility on new data.
  React.useEffect(() => {
    setVisibleTitles(null);
  }, [rows, groupCol]);

  if (!dateCol || charts.length === 0) {
    return <Box variant="p">Chart is not available for this result shape.</Box>;
  }

  const multiSeries = allTitles.length >= 2;
  const isVisible = (title: string) =>
    visibleTitles === null || visibleTitles.includes(title);

  const filterLabel = groupNoun(groupCol); // "FGIDs" or "Sites"
  const selectAll = () => setVisibleTitles(allTitles);
  const deselectAll = () => setVisibleTitles([]);

  return (
    <SpaceBetween size="l">
      {capped && (
        <Box variant="p" color="text-status-info">
          Showing top {MAX_GROUPS} of {total}. Download CSV from table for full data.
        </Box>
      )}
      {charts.map(({ title, col }) => {
        const { series, xDomain } = buildSeries(displayRows, dateCol, col, groupCol);
        const visibleSeries = series.filter((s) => isVisible(s.title));
        const fileName = `freshsight_${viewType}_${business}_${region}_${col}_${todayString()}.png`;
        return (
          <div key={title}>
            <Header
              variant="h3"
              actions={
                <Button
                  iconName="download"
                  onClick={() => downloadChartPng(chartRefs.current[title], fileName)}
                >
                  Download Chart
                </Button>
              }
            >
              {title}
            </Header>
            <div ref={(el) => (chartRefs.current[title] = el)}>
              <LineChart
                series={series}
                visibleSeries={visibleSeries}
                onFilterChange={({ detail }) =>
                  setVisibleTitles(detail.visibleSeries.map((s) => s.title))
                }
                xDomain={xDomain}
                xScaleType="categorical"
                height={280}
                xTitle="Date"
                yTitle={title}
                hideFilter={series.length <= 1}
                i18nStrings={multiSeries ? { filterLabel } : undefined}
                additionalFilters={
                  multiSeries ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button onClick={selectAll}>Select all</Button>
                      <Button onClick={deselectAll}>Deselect all</Button>
                    </SpaceBetween>
                  ) : undefined
                }
                empty={<Box textAlign="center">No data</Box>}
                noMatch={<Box textAlign="center">No visible series</Box>}
              />
            </div>
          </div>
        );
      })}
    </SpaceBetween>
  );
}
